import { FilterState, useIsAnyFilterActive } from '@Component/Filter/utils';
import { ColumnType, OrderByTypeEmployeeOverview, TableSortState } from '@Hooks/useOrdering';
import { EmployeeDTO, IdAndDisplayName } from '@Models/model.types';
import { EmployeeColumnList } from '@Page/Overview/OverviewTable';
import {
    APIError,
    BatchActionMoreButton,
    BatchActions,
    BatchActionsButton,
    BatchActionsButtons,
    BatchActionsTitle,
    InlineMarkdown,
    ListResponse,
    UseFetchPaginatedState,
} from '@tlx/astro-shared';
import {
    Body,
    ButtonProps,
    Header,
    PopupMenuItemButton,
    Row,
    RowAlert,
    Table,
    TableTopBar,
    TestableProps,
} from '@tlx/atlas';
import React, { ComponentType, Dispatch, MutableRefObject, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { NewTableEmptyCreateEmployee } from '@Component/EmployeeTable/TableStates/TableEmptyCreateEmployee.tsx';
import { NewTableEmptyResetFilter } from '@Component/EmployeeTable/TableStates/TableEmptyResetFilter.tsx';
import { TableError } from '@Component/EmployeeTable/TableStates/TableError.tsx';
import { SkeletonRows } from '@Component/LoadMore/SkeletonRows.tsx';
import { DisplayEmployee } from '@Component/Table/DisplayEmployee';
import { DisplaySelectRow } from '@Component/Table/DisplaySelectRow';
import {
    DivisionDisplay,
    LoadMoreTargetRow,
    TextCellDash,
    TextCellDashMoneyFormat,
} from '@Component/Table/Table.utils.tsx';
import { usePagesPilotFeatureFlags } from '@Hooks/dataFetching/usePagesPilotFeatureFlags';
import { AUTH_LEVEL_DEPARTMENT, useTskContext } from '@Hooks/useTskContext.ts';
import { LocaleKey } from '@Locale/locale.ts';
import { EmployeeOverviewTab } from '@Page/EmployeeOverview/EmployeeOverview';
import { EmployeeOverviewTableSettings } from '@Page/EmployeeOverview/EmployeeOverviewColumnSettings';
import { EmployeeOverviewFilters } from '@Page/EmployeeOverview/EmployeeOverviewFilters';
import { SelectableMachinery } from '@Page/EmployeeOverview/hooks/useSelectableEmployees';
import { BatchActionModals, ModalType } from '@Page/EmployeeOverview/modals/BatchActionModals';
import { TaxcardStatus } from '@Page/EmployeeOverview/Taxcard/TaxcardStatus';
import { useTableColumnsSettingTabs } from '@Page/EmployeeOverview/useTableColumnsSettingTabs';
import classNames from 'classnames';
import { SWRInfiniteKeyedMutator } from 'swr/infinite';

type BatchActionButtonConfig = {
    query: string;
    onClick: React.MouseEventHandler<HTMLElement>;
    textKey: LocaleKey;
} & TestableProps;

interface EmployeeOverviewTableProps {
    scrollableContainerRef: MutableRefObject<HTMLDivElement | null>;
    filterState: FilterState;
    checkMachinery: SelectableMachinery;
    items: UseFetchPaginatedState<EmployeeDTO, APIError> & {
        mutate: SWRInfiniteKeyedMutator<ListResponse<EmployeeDTO>[]>;
    };
    ordering: TableSortState<OrderByTypeEmployeeOverview>;
    validationsMap: Map<number, string[]>;
    setValidationsMap: Dispatch<SetStateAction<Map<number, string[]>>>;
    handleDownload: () => void;
    pendingDownload: boolean;
}

export const EmployeeOverviewTable: React.FC<EmployeeOverviewTableProps> = ({
    scrollableContainerRef,
    filterState: filters,
    checkMachinery,
    items,
    ordering,
    validationsMap,
    setValidationsMap,
    handleDownload,
    pendingDownload,
}) => {
    const [activeModal, setActiveModal] = useState<ModalType | null>(null);

    const showBatchActions = checkMachinery.state.selectedEmployees.length > 0;
    const columns = useTableColumnsSettingTabs(filters);

    const onSelectedRowsChange = (selectedRows?: string[]): void => {
        if (selectedRows) {
            const newSelectedRows = selectedRows.map(Number);
            checkMachinery.onCheckRows(newSelectedRows.map(String));
        }
    };

    //this part is displaying one primary button based off the query, and the rest in the kebab

    const batchActionButtons: BatchActionButtonConfig[] = [
        /* {
            query: 'never',
            ['data-testid']: 'batch-actions-assign-entitlements',
            onClick: () => setActiveModal('assignEntitlements'),
            textKey: 'text_assign_privileges_short',
        }, */
        {
            query: '',
            ['data-testid']: 'batch-actions-change-employee-info',
            onClick: () => setActiveModal('changeEmployeeInfo'),
            textKey: 'text_change_employee_info',
        },
        {
            query: 'salary',
            ['data-testid']: 'batch-actions-adjust-wages',
            onClick: () => setActiveModal('wageAdjustment'),
            textKey: 'text_adjust_wages',
        },
        /* {
            query: 'taxcard',
            ['data-testid']: 'batch-actions-fetch-taxcards',
            onClick: () => {
                setTaxcardModalOpen(true);
            },
            textKey: 'text_get_taxcards',
        }, */
        {
            query: 'never',
            ['data-testid']: 'batch-actions-add-additions-deductions',
            onClick: () => setActiveModal('additionsDeductionsn'),
            textKey: 'text_add_standard_additions_deductions',
        },
        {
            query: 'never',
            ['data-testid']: 'batch-actions-add-union-deduction-fees',
            onClick: () => setActiveModal('unionDeduction'),
            textKey: 'text_add_union_deductions',
        },
    ];

    const { data } = usePagesPilotFeatureFlags();

    if (data?.value.EndEmploymentsBatchAction) {
        batchActionButtons.push({
            query: 'never',
            ['data-testid']: 'batch-actions-add-end-employments',
            onClick: () => setActiveModal('endEmployments'),
            textKey: 'tsk_end_employments',
        });
    }

    return (
        <>
            <TableTopBar>
                {showBatchActions ? (
                    <BatchActions>
                        <BatchActionsTitle>
                            {checkMachinery.state.selectedEmployees.length} <FormattedMessage id="text_selected" />
                        </BatchActionsTitle>
                        <BatchActionsButtons>
                            <BatchActionModals
                                activeModal={activeModal}
                                onClose={() => setActiveModal(null)}
                                employeeIds={checkMachinery.state.selectedEmployees}
                                setValidationsMap={setValidationsMap}
                                mutate={items.mutate}
                            />
                            {batchActionButtons.map((config, index) => (
                                <div key={index}>
                                    {filters.viewFilter.query === config.query && (
                                        <ButtonRenderer ButtonComponent={BatchActionsButton} config={config} />
                                    )}
                                </div>
                            ))}
                            <BatchActionMoreButton data-testid="batch-actions-more-button">
                                {batchActionButtons.map((config, index) => (
                                    <div key={index}>
                                        {filters.viewFilter.query !== config.query && (
                                            <ButtonRenderer ButtonComponent={PopupMenuItemButton} config={config} />
                                        )}
                                    </div>
                                ))}
                            </BatchActionMoreButton>
                        </BatchActionsButtons>
                    </BatchActions>
                ) : (
                    <>
                        <EmployeeOverviewFilters filterState={filters} />
                        <EmployeeOverviewTableSettings
                            tab={filters.viewFilter.query as EmployeeOverviewTab}
                            handleDownload={handleDownload}
                            pendingDownload={pendingDownload}
                        />
                    </>
                )}
            </TableTopBar>
            <Table
                sort={ordering.sort}
                onSortChange={ordering.onSortChange}
                density={'compact'}
                className={classNames('employee-overview__table')}
                selectedRows={checkMachinery.state.selectedEmployees.map(String)}
                onSelectedRowsChange={onSelectedRowsChange}
            >
                <Header>
                    <Row>
                        <EmployeeColumnList
                            columns={columns}
                            isCheckedAll={checkMachinery.allChecked}
                            onCheckAll={checkMachinery.onCheckAll}
                            someChecked={checkMachinery.someChecked}
                            selectAllState={checkMachinery.selectAllState}
                        />
                    </Row>
                </Header>
                <TableBodyWrapper
                    items={items}
                    columns={columns}
                    scrollableContainerRef={scrollableContainerRef}
                    filters={filters}
                    validationsMap={validationsMap}
                />
            </Table>
        </>
    );
};

interface TableBodyWrapperProps {
    items: UseFetchPaginatedState<EmployeeDTO, APIError>;
    columns: ColumnType[];
    scrollableContainerRef: MutableRefObject<HTMLDivElement | null>;
    filters: FilterState;
    validationsMap: Map<number, string[]>;
}

const TableBodyWrapper: React.FC<TableBodyWrapperProps> = ({
    items,
    columns,
    scrollableContainerRef,
    filters,
    validationsMap,
}) => {
    const isFilterAnyActive = useIsAnyFilterActive(filters);
    const { authLevelEmployeeOrWage } = useTskContext();
    return (
        <Body>
            {items.data.map((item) => {
                return (
                    <Row
                        id={item.id.toString()}
                        key={item.id}
                        className={'atl-items-center'}
                        variant={validationsMap.has(item.id) ? 'error' : undefined}
                    >
                        <RowColumns columns={columns} item={item} currentTab={filters.viewFilter.query} />
                        {validationsMap.has(item.id) &&
                            validationsMap.get(item.id)?.map((error, index) => (
                                <RowAlert key={index} variant="error">
                                    <InlineMarkdown>{error}</InlineMarkdown>
                                </RowAlert>
                            ))}
                    </Row>
                );
            })}
            {!items.isLoading &&
                items.isEmpty &&
                (isFilterAnyActive ? <NewTableEmptyResetFilter {...filters} /> : <NewTableEmptyCreateEmployee />)}
            {items.error && <TableError />}
            {items.isLoading && (
                <SkeletonRows
                    numberOfColumns={columns.length + 1}
                    numberOfActions={0}
                    checkbox={authLevelEmployeeOrWage >= AUTH_LEVEL_DEPARTMENT}
                />
            )}
            {!items.isLoading && items.hasMore && (
                <LoadMoreTargetRow
                    hasMore={items.hasMore}
                    scrollContainerRef={scrollableContainerRef}
                    onLoadMore={items.loadMore}
                />
            )}
        </Body>
    );
};

const RowColumns = ({
    columns,
    item,
    currentTab,
}: {
    columns: ColumnType[];
    item: EmployeeDTO;
    currentTab: string;
}) => {
    const intl = useIntl();
    const { authLevelEmployeeOrWage, userDepartmentId } = useTskContext();

    const getEmployeeApproverDisplay = (
        employeeApprover: IdAndDisplayName | undefined,
        relationType: 'CUSTOM' | 'DEPARTMENT_MANAGER' | undefined,
    ): string => {
        if (relationType === 'DEPARTMENT_MANAGER') {
            const roleLabel = intl.formatMessage({
                id: 'text_department_manager',
            });
            const personName = employeeApprover?.displayName
                ? employeeApprover.displayName
                : intl.formatMessage({ id: 'text_csv_qualifier_none' });
            return `${roleLabel} (${personName})`;
        }
        if (!employeeApprover?.displayName) {
            return intl.formatMessage({ id: 'text_csv_qualifier_none' });
        }
        return employeeApprover.displayName;
    };

    return (
        <>
            {authLevelEmployeeOrWage >= AUTH_LEVEL_DEPARTMENT && (
                <DisplaySelectRow
                    disabled={
                        authLevelEmployeeOrWage <= AUTH_LEVEL_DEPARTMENT && userDepartmentId != item.department.id
                    }
                />
            )}
            <TextCellDash>
                <DisplayEmployee item={item} currentTab={currentTab} />
            </TextCellDash>
            {columns.map((column) => (
                <div key={column.id}>
                    {(() => {
                        const advanceTaxcard = item.taxcard?.advanceTaxcards[0];
                        switch (column.id) {
                            case 'DEPARTMENT':
                                return <TextCellDash>{item.department?.displayName}</TextCellDash>;
                            case 'NUMBER':
                                return <TextCellDash>{item.number}</TextCellDash>;
                            case 'NATIONAL_IDENTITY_NUMBER':
                                return <TextCellDash>{item.socialSecurityNumberOrDNumber}</TextCellDash>;
                            case 'DATE_OF_BIRTH':
                                return <TextCellDash>{item.dateOfBirth}</TextCellDash>;
                            case 'EMAIL':
                                return <TextCellDash>{item.email}</TextCellDash>;
                            case 'PHONE':
                                return <TextCellDash>{item.phoneNumberMobile}</TextCellDash>;
                            case 'PHONE_WORK':
                                return <TextCellDash>{item.phoneNumberWork}</TextCellDash>;
                            case 'ADDRESS':
                                return <TextCellDash>{item.address?.addressAsString}</TextCellDash>;
                            case 'RATE':
                                return (
                                    <TextCellDash>
                                        {item.currentCompanyEmployeeRate?.hourlyRate !== 0 &&
                                            item.currentCompanyEmployeeRate?.hourlyRate}
                                    </TextCellDash>
                                );
                            case 'HOUR_COST_RATE':
                                return (
                                    <TextCellDash>
                                        {item.currentCompanyEmployeeRate?.hourlyCost !== 0 &&
                                            item.currentCompanyEmployeeRate?.hourlyCost}
                                    </TextCellDash>
                                );
                            case 'STARTDATE':
                                return (
                                    <TextCellDash>
                                        <div className="atl-py-8">
                                            <div>
                                                {item.lastActiveEmployment?.startDate}
                                                {item.lastActiveEmployment?.endDate && ' \u2014'}
                                                {/* en dash ( — ) */}
                                            </div>
                                            <div>{item.lastActiveEmployment?.endDate}</div>
                                        </div>
                                    </TextCellDash>
                                );
                            case 'DIVISION':
                                return (
                                    <TextCellDash>
                                        <DivisionDisplay division={item.lastActiveEmployment?.division} />
                                    </TextCellDash>
                                );
                            case 'REMUNERATION_TYPE':
                                return (
                                    <TextCellDash>
                                        {item.lastActiveEmployment?.latestSalary?.remunerationType &&
                                            intl.formatMessage({
                                                id: getTextKeyForRemunerationType(
                                                    item.lastActiveEmployment?.latestSalary?.remunerationType,
                                                ),
                                            })}
                                    </TextCellDash>
                                );
                            case 'EMPLOYMENT_FORM':
                                return (
                                    <TextCellDash>
                                        {item.lastActiveEmployment?.latestSalary?.employmentForm &&
                                            intl.formatMessage({
                                                id: getTextKeyForEmploymentForm(
                                                    item.lastActiveEmployment?.latestSalary?.employmentForm,
                                                ),
                                            })}
                                    </TextCellDash>
                                );
                            case 'EMPLOYMENT_TYPE':
                                return (
                                    <TextCellDash>
                                        {item.lastActiveEmployment?.latestSalary?.employmentType &&
                                            intl.formatMessage({
                                                id: getTextKeyForEmploymentType(
                                                    item.lastActiveEmployment?.latestSalary?.employmentType,
                                                ),
                                            })}
                                    </TextCellDash>
                                );
                            case 'PERCENT_OF_EMPLOYMENT':
                                return (
                                    <TextCellDash>
                                        {item.lastActiveEmployment?.latestSalary?.percentageOfFullTimeEquivalent !==
                                            0 &&
                                            item.lastActiveEmployment?.latestSalary?.percentageOfFullTimeEquivalent}
                                    </TextCellDash>
                                );
                            case 'ANNUAL_SALARY':
                                return (
                                    <TextCellDashMoneyFormat>
                                        {item.lastActiveEmployment?.latestSalary?.annualSalary ?? 0}
                                    </TextCellDashMoneyFormat>
                                );
                            case 'MONTHLY_SALARY':
                                return (
                                    <TextCellDashMoneyFormat>
                                        {item.lastActiveEmployment?.latestSalary?.monthlySalary ?? 0}
                                    </TextCellDashMoneyFormat>
                                );
                            case 'HOURLY_WAGE':
                                return (
                                    <TextCellDashMoneyFormat>
                                        {item.lastActiveEmployment?.latestSalary?.hourlyWage ?? 0}
                                    </TextCellDashMoneyFormat>
                                );
                            case 'DELIVERY_METHOD_WAGE_SLIP':
                                return <TextCellDash>{item.deliveryMethodWageSlipString}</TextCellDash>;
                            case 'BANK_ACCOUNT_NUMBER':
                                return <TextCellDash>{item.bankAccountNumber}</TextCellDash>;
                            case 'MUNICIPALITY_ENI':
                                return (
                                    <TextCellDash>
                                        {item.lastActiveEmployment?.latestSalary?.payrollTaxMunicipalityId?.displayName}
                                    </TextCellDash>
                                );
                            case 'STATUS':
                                return (
                                    <TextCellDash>
                                        {item.taxcard && <TaxcardStatus taxcard={item.taxcard} />}
                                    </TextCellDash>
                                );
                            case 'DEDUCTION_CODE':
                                return <TextCellDash>{advanceTaxcard?.trekkodeDescription}</TextCellDash>;
                            case 'TYPE':
                                return <TextCellDash>{advanceTaxcard?.typeDescription}</TextCellDash>;
                            case 'TABLE_NUMBER':
                                return <TextCellDash>{advanceTaxcard?.tabellnummer}</TextCellDash>;
                            case 'PERCENTAGE_RATE':
                                return <TextCellDash>{advanceTaxcard?.prosentsats}</TextCellDash>;
                            case 'FREE_CARD_AMOUNT':
                                return (
                                    <TextCellDash>
                                        {`${
                                            advanceTaxcard?.remainingFreeCardAmount != undefined &&
                                            advanceTaxcard?.frikortbelop != undefined &&
                                            advanceTaxcard?.frikortbelop > 0 &&
                                            advanceTaxcard?.remainingFreeCardAmount != advanceTaxcard?.frikortbelop
                                                ? `${advanceTaxcard?.remainingFreeCardAmount} ${intl.formatMessage({
                                                      id: 'text_of_lc',
                                                  })} `
                                                : ''
                                        }${displayBlankIfZero(advanceTaxcard?.frikortbelop)}`}
                                    </TextCellDash>
                                );
                            case 'ADDITIONAL_INFO':
                                return <TextCellDash>{item.taxcard?.additionalInfo}</TextCellDash>;
                            case 'LAST_CHANGED':
                                return <TextCellDash>{item.taxcard?.utstedtDato}</TextCellDash>;
                            case 'ALLOW_LOGIN':
                                return (
                                    <TextCellDash>
                                        {(() => {
                                            if (item.allowLogin) {
                                                if (item.authReadOnly) {
                                                    return intl.formatMessage({
                                                        id: 'text_reading_access',
                                                    });
                                                } else if (item.isPayslipOnly) {
                                                    return intl.formatMessage({
                                                        id: 'text_payslip_only_access',
                                                    });
                                                } else {
                                                    return intl.formatMessage({
                                                        id: 'text_yes',
                                                    });
                                                }
                                            } else {
                                                return intl.formatMessage({
                                                    id: 'text_no',
                                                });
                                            }
                                        })()}
                                    </TextCellDash>
                                );
                            case 'OCCUPATION_CODE':
                                return (
                                    <TextCellDash>
                                        {item.lastActiveEmployment?.latestSalary?.occupationCode
                                            ? `${item.lastActiveEmployment?.latestSalary?.occupationCode.code} ${item.lastActiveEmployment?.latestSalary?.occupationCode.nameNO}`
                                            : ''}
                                    </TextCellDash>
                                );
                            case 'VISMA_2FA_CONNECT':
                                return (
                                    <TextCellDash>
                                        {item.vismaConnect2FAactive
                                            ? intl.formatMessage({
                                                  id: 'text_yes',
                                              })
                                            : intl.formatMessage({
                                                  id: 'text_no',
                                              })}
                                    </TextCellDash>
                                );
                            case 'USER_ADMINISTRATOR':
                                return (
                                    <TextCellDash>
                                        {item.isUserAdministrator
                                            ? intl.formatMessage({
                                                  id: 'text_yes',
                                              })
                                            : intl.formatMessage({
                                                  id: 'text_no',
                                              })}
                                    </TextCellDash>
                                );
                            case 'ALLOW_REG_INFO':
                                return (
                                    <TextCellDash>
                                        {item.allowRegInfo
                                            ? intl.formatMessage({
                                                  id: 'text_yes',
                                              })
                                            : intl.formatMessage({
                                                  id: 'text_no',
                                              })}
                                    </TextCellDash>
                                );
                            case 'EMPLOYEE_APPROVER':
                                return (
                                    <TextCellDash>
                                        {getEmployeeApproverDisplay(
                                            item.employeeApprover,
                                            item.employeeApproverRelationType,
                                        )}
                                    </TextCellDash>
                                );
                            default:
                                return null;
                        }
                    })()}
                </div>
            ))}
        </>
    );
};

function displayBlankIfZero(number: number | undefined) {
    if (number == undefined || number <= 1) {
        return '';
    }
    return number;
}

type BatchActionButtonComponent = ComponentType<
    ButtonProps & {
        variant?: 'primary' | 'secondary';
    }
>;

const ButtonRenderer: React.FC<{
    ButtonComponent: BatchActionButtonComponent;
    config: BatchActionButtonConfig;
}> = ({ ButtonComponent, config }) => (
    <ButtonComponent data-testid={config['data-testid']} onClick={config.onClick} variant="secondary">
        <FormattedMessage id={config.textKey} />
    </ButtonComponent>
);

function getTextKeyForRemunerationType(remunerationType: string | undefined | null): LocaleKey | undefined {
    switch (remunerationType) {
        case 'MONTHLY_WAGE':
            return 'text_regular_salary_transactions';
        case 'HOURLY_WAGE':
            return 'text_hourly_wage';
        case 'COMMISION_PERCENTAGE':
            return 'text_salary_form_commission_percentage';
        case 'FEE':
            return 'text_fee';
        case 'NOT_CHOSEN':
            return 'option_not_chosen';
        case 'PIECEWORK_WAGE':
            return 'text_salary_form_piecework_wage';
        default:
            return undefined;
    }
}

function getTextKeyForEmploymentForm(employmentForm: string | undefined | null): LocaleKey | undefined {
    switch (employmentForm) {
        case 'PERMANENT':
            return 'text_employment_form_permanent';
        case 'TEMPORARY':
            return 'text_employment_form_temporary';
        case 'PERMANENT_AND_HIRED_OUT':
            return 'text_employment_form_permanent_hired_out';
        case 'TEMPORARY_AND_HIRED_OUT':
            return 'text_employment_form_temporary_hired_out';
        case 'TEMPORARY_ON_CALL':
            return 'text_employment_form_temporary_on_call';
        case 'NOT_CHOSEN':
            return 'option_not_chosen';
        default:
            return undefined;
    }
}

function getTextKeyForEmploymentType(employmentType: string | undefined | null): LocaleKey | undefined {
    switch (employmentType) {
        case 'ORDINARY':
            return 'text_employment_type_ordinary';
        case 'MARITIME':
            return 'text_employment_type_maritime';
        case 'FREELANCE':
            return 'text_employment_type_freelancer';
        case 'NOT_CHOSEN':
            return 'option_not_chosen';
        default:
            return undefined;
    }
}
